import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Search, Building2, Mail, ChevronLeft, Send,
  CheckCircle2, Clock, XCircle, AlertCircle, Star,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const t = localStorage.getItem('session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STATUS_META = {
  pending: { label: 'Sent', icon: Clock, colour: 'bg-blue-100 text-blue-800' },
  viewed: { label: 'Viewed', icon: AlertCircle, colour: 'bg-purple-100 text-purple-800' },
  accepted: { label: 'Accepted', icon: CheckCircle2, colour: 'bg-green-100 text-green-800' },
  declined: { label: 'Declined', icon: XCircle, colour: 'bg-red-100 text-red-800' },
  expired: { label: 'Expired', icon: XCircle, colour: 'bg-gray-200 text-gray-700' },
};

export default function InviteManagingAgent() {
  const { hoaId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [search, setSearch] = useState('');
  const [city, setCity] = useState('');
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [replaceCandidate, setReplaceCandidate] = useState(null);

  const [newCompany, setNewCompany] = useState({
    company_name: '', contact_person: '', contact_email: '', contact_phone: '', message: '',
  });

  const fetchHistory = useCallback(async () => {
    if (!hoaId) return;
    const r = await fetch(`${API}/api/company-invitations/sent/${hoaId}`, {
      credentials: 'include', headers: authHeaders(),
    });
    if (r.ok) setHistory((await r.json()).invitations || []);
  }, [hoaId]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const runSearch = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set('q', search);
    if (city) params.set('city', city);
    const r = await fetch(`${API}/api/company-invitations/search-companies?${params}`, {
      credentials: 'include', headers: authHeaders(),
    });
    if (r.ok) setCompanies((await r.json()).companies || []);
    setLoading(false);
  };

  const inviteExisting = async (company, replace = false) => {
    const r = await fetch(`${API}/api/company-invitations/invite-existing`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ hoa_id: hoaId, company_id: company.company_id, replace }),
    });
    if (r.ok) {
      toast.success(`Invitation sent to ${company.name}`);
      setReplaceCandidate(null);
      fetchHistory();
    } else {
      const err = await r.json().catch(() => ({}));
      if (err.detail?.error === 'existing_assignment') {
        setReplaceCandidate({ company, current: err.detail });
      } else {
        toast.error(typeof err.detail === 'string' ? err.detail : (err.detail?.message || 'Invite failed'));
      }
    }
  };

  const inviteNew = async () => {
    if (!newCompany.company_name || !newCompany.contact_email) {
      return toast.error('Company name and email are required');
    }
    const r = await fetch(`${API}/api/company-invitations/invite-new`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ hoa_id: hoaId, ...newCompany }),
    });
    if (r.ok) {
      toast.success('Invitation sent — they will receive an email');
      setNewCompany({ company_name: '', contact_person: '', contact_email: '', contact_phone: '', message: '' });
      fetchHistory();
    } else {
      const err = await r.json().catch(() => ({}));
      toast.error(typeof err.detail === 'string' ? err.detail : (err.detail?.message || 'Invite failed'));
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8" data-testid="invite-agent-page">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded hover:bg-gray-200">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
              <Send className="w-7 h-7 text-indigo-600" /> Invite Your Managing Agent
            </h1>
            <p className="text-gray-600 text-sm">Link a property management company to this building.</p>
          </div>
        </div>

        {/* Benefits banner */}
        <Card className="mb-6 bg-gradient-to-br from-indigo-50 to-emerald-50 border-indigo-200">
          <CardContent className="p-5">
            <h3 className="font-semibold mb-2">Why invite your managing agent?</h3>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-700">
              <li>✓ Better communication with residents</li>
              <li>✓ Centralised document storage</li>
              <li>✓ Maintenance ticket tracking</li>
              <li>✓ Insurance claim management</li>
              <li>✓ Higher resident engagement</li>
              <li>✓ Same building — no duplicate setup</li>
            </ul>
          </CardContent>
        </Card>

        <Tabs defaultValue="search">
          <TabsList>
            <TabsTrigger value="search" data-testid="tab-search">Search Existing</TabsTrigger>
            <TabsTrigger value="new" data-testid="tab-new">Invite New Company</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">
              History {history.length > 0 && <Badge className="ml-2">{history.length}</Badge>}
            </TabsTrigger>
          </TabsList>

          {/* SEARCH */}
          <TabsContent value="search" className="mt-4">
            <Card className="mb-4">
              <CardContent className="p-4 flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Company name (Maxprop, Trafalgar, Wakefields...)"
                    className="pl-9"
                    onKeyDown={(e) => e.key === 'Enter' && runSearch()}
                    data-testid="search-input"
                  />
                </div>
                <Input
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="City"
                  className="w-40"
                  data-testid="city-input"
                />
                <Button onClick={runSearch} data-testid="search-btn">
                  <Search className="w-4 h-4 mr-1" /> Search
                </Button>
              </CardContent>
            </Card>

            {loading ? (
              <Card><CardContent className="p-8 text-center text-gray-500">Searching…</CardContent></Card>
            ) : companies.length === 0 ? (
              <Card><CardContent className="p-8 text-center text-gray-500">
                {search ? 'No companies match. Try a different name or use "Invite New Company".' : 'Type a company name above and click Search.'}
              </CardContent></Card>
            ) : (
              <div className="space-y-2">
                {companies.map((c) => (
                  <Card key={c.company_id} data-testid={`company-row-${c.company_id}`}>
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className="p-2 bg-indigo-50 rounded-lg">
                        <Building2 className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium flex items-center gap-2">
                          {c.name}
                          {c.is_partner && (
                            <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">
                              <Star className="w-3 h-3 mr-1 fill-current" /> Preferred
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">
                          {[c.city, c.region, c.country].filter(Boolean).join(' · ') || 'Region unknown'}
                        </div>
                      </div>
                      <Button onClick={() => inviteExisting(c)} data-testid={`invite-${c.company_id}`}>
                        <Send className="w-4 h-4 mr-1" /> Invite to Manage
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* NEW */}
          <TabsContent value="new" className="mt-4">
            <Card>
              <CardContent className="p-5 space-y-3">
                <p className="text-sm text-gray-600">
                  Your managing agent isn't on BodyCorp365 yet? Send them a personal invitation.
                </p>
                <div><Label>Company name *</Label><Input value={newCompany.company_name} onChange={(e) => setNewCompany({ ...newCompany, company_name: e.target.value })} data-testid="new-company-name" /></div>
                <div><Label>Contact person</Label><Input value={newCompany.contact_person} onChange={(e) => setNewCompany({ ...newCompany, contact_person: e.target.value })} data-testid="new-contact-person" /></div>
                <div><Label>Contact email *</Label><Input type="email" value={newCompany.contact_email} onChange={(e) => setNewCompany({ ...newCompany, contact_email: e.target.value })} data-testid="new-contact-email" /></div>
                <div><Label>Contact phone</Label><Input value={newCompany.contact_phone} onChange={(e) => setNewCompany({ ...newCompany, contact_phone: e.target.value })} data-testid="new-contact-phone" /></div>
                <div><Label>Personal message (optional)</Label><Textarea rows={4} value={newCompany.message} onChange={(e) => setNewCompany({ ...newCompany, message: e.target.value })} placeholder="Hi team, we'd love to onboard our building..." data-testid="new-message" /></div>
                <Button onClick={inviteNew} className="w-full" data-testid="send-new-invite"><Send className="w-4 h-4 mr-1" /> Send Invitation</Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* HISTORY */}
          <TabsContent value="history" className="mt-4">
            {history.length === 0 ? (
              <Card><CardContent className="p-8 text-center text-gray-500">No invitations sent yet.</CardContent></Card>
            ) : (
              <div className="space-y-2">
                {history.map((h) => {
                  const meta = STATUS_META[h.status] || STATUS_META.pending;
                  const Icon = meta.icon;
                  return (
                    <Card key={h.invitation_id} data-testid={`history-${h.invitation_id}`}>
                      <CardContent className="p-4 flex items-center gap-3">
                        <Badge variant="outline" className={meta.colour}><Icon className="w-3 h-3 mr-1" /> {meta.label}</Badge>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{h.company_name}</div>
                          <div className="text-xs text-gray-500">
                            Invited {new Date(h.created_at).toLocaleString()}
                            {h.viewed_at && ` · Viewed ${new Date(h.viewed_at).toLocaleString()}`}
                            {h.accepted_at && ` · Accepted ${new Date(h.accepted_at).toLocaleString()}`}
                            {h.declined_at && ` · Declined`}
                          </div>
                          {h.decline_reason && <div className="text-xs text-red-600 mt-1">"{h.decline_reason}"</div>}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Replace confirmation */}
      <AlertDialog open={!!replaceCandidate} onOpenChange={(o) => !o && setReplaceCandidate(null)}>
        <AlertDialogContent data-testid="replace-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>This building is already managed</AlertDialogTitle>
            <AlertDialogDescription>
              Your building is currently linked to <strong>{replaceCandidate?.current?.current_company_name}</strong>.
              Do you want to replace them with <strong>{replaceCandidate?.company?.name}</strong>?
              <br /><br />
              The new company will take over the management layer only when they accept.
              Your building data, residents, trustees and history will remain intact.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="replace-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => replaceCandidate && inviteExisting(replaceCandidate.company, true)}
              data-testid="replace-confirm"
            >
              Yes, Send Invitation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
